/**
 * Recover what a person typed.
 *
 * A form keeps a copy of its fields in this browser while the person types.
 * When they come back, a banner offers the copy: keep it or discard it. The
 * copy is a convenience of this browser only; it never reaches the server and
 * never appears in a change log. A successful submit clears it.
 */

import { type FormEvent, type FormHTMLAttributes, type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { Button } from "./primitives.tsx";

const PREFIX = "arcgrc.recover.";
const DEBOUNCE_MS = 400;

interface Stored {
  readonly at: string;
  readonly values: Record<string, string>;
}

function read(key: string): Stored | null {
  try {
    const raw = window.localStorage.getItem(PREFIX + key);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Stored;
    return typeof parsed.at === "string" && parsed.values !== null && typeof parsed.values === "object" ? parsed : null;
  } catch { return null; }
}

function write(key: string, values: Record<string, string>): void {
  try {
    const kept = Object.fromEntries(Object.entries(values).filter(([, value]) => value.trim() !== ""));
    if (Object.keys(kept).length === 0) { window.localStorage.removeItem(PREFIX + key); return; }
    window.localStorage.setItem(PREFIX + key, JSON.stringify({ at: new Date().toISOString(), values: kept } satisfies Stored));
  } catch { /* A private window keeps nothing. */ }
}

/** Forget the copy. Call it after a successful submit. */
export function clearRecovery(key: string): void {
  try { window.localStorage.removeItem(PREFIX + key); } catch { /* nothing to clear */ }
}

function when(at: string): string {
  const date = new Date(at);
  return Number.isNaN(date.getTime()) ? "earlier" : date.toLocaleString();
}

/** The banner: keep or discard. */
export function RecoveryBanner({ at, onKeep, onDiscard }: { readonly at: string; readonly onKeep: () => void; readonly onDiscard: () => void }) {
  return (
    <div role="note" className="flex flex-wrap items-center gap-3 rounded-arc border border-info bg-paper px-3 py-2 text-sm" data-testid="recovery-banner">
      <span>{`Recovered what you typed on ${when(at)} and had not saved.`}</span>
      <Button size="small" variant="primary" onClick={onKeep}>Keep it</Button>
      <Button size="small" variant="quiet" onClick={onDiscard}>Discard</Button>
    </div>
  );
}

function fieldValues(form: HTMLFormElement): Record<string, string> {
  const values: Record<string, string> = {};
  for (const element of Array.from(form.elements)) {
    if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement)) continue;
    if (!element.name) continue;
    if (element instanceof HTMLInputElement && (element.type === "file" || element.type === "password" || element.type === "hidden")) continue;
    if (element instanceof HTMLInputElement && (element.type === "checkbox" || element.type === "radio")) {
      values[element.name] = element.checked ? "on" : "";
      continue;
    }
    values[element.name] = element.value;
  }
  return values;
}

function setNativeValue(element: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, value: string): void {
  const prototype = element instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype
    : element instanceof HTMLSelectElement ? HTMLSelectElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(prototype, "value")?.set;
  if (setter) setter.call(element, value); else element.value = value;
  element.dispatchEvent(new Event("input", { bubbles: true }));
  element.dispatchEvent(new Event("change", { bubbles: true }));
}

/**
 * A form that remembers its named fields while the person types.
 *
 * Every field that should be recovered needs a `name`. On keep, the values go
 * back into the fields; a controlled input hears an input event, so its state
 * follows. `onRestore` receives the values too, for state the fields do not
 * carry.
 */
export function RecoverableForm({
  storageKey, onRestore, children, onSubmit, ...rest
}: FormHTMLAttributes<HTMLFormElement> & { readonly storageKey: string; readonly onRestore?: ((values: Record<string, string>) => void) | undefined; readonly children: ReactNode }) {
  const ref = useRef<HTMLFormElement | null>(null);
  const timer = useRef<number | null>(null);
  const [offer, setOffer] = useState<Stored | null>(null);

  useEffect(() => {
    const stored = read(storageKey);
    if (stored === null) return;
    const form = ref.current;
    const current = form ? fieldValues(form) : {};
    const differs = Object.entries(stored.values).some(([name, value]) => (current[name] ?? "") !== value);
    setOffer(differs ? stored : null);
  }, [storageKey]);

  const remember = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      if (ref.current) write(storageKey, fieldValues(ref.current));
    }, DEBOUNCE_MS);
  }, [storageKey]);

  const keep = () => {
    const form = ref.current;
    if (form && offer) {
      for (const element of Array.from(form.elements)) {
        if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement)) continue;
        const value = offer.values[element.name];
        if (value === undefined) continue;
        if (element instanceof HTMLInputElement && (element.type === "checkbox" || element.type === "radio")) {
          if (element.checked !== (value === "on")) element.click();
          continue;
        }
        setNativeValue(element, value);
      }
      onRestore?.(offer.values);
    }
    setOffer(null);
  };
  const discard = () => { clearRecovery(storageKey); setOffer(null); };

  return (
    <form
      {...rest}
      ref={ref}
      onInput={remember}
      onSubmit={(event: FormEvent<HTMLFormElement>) => { onSubmit?.(event); }}
    >
      {offer !== null && <div className="md:col-span-2 lg:col-span-3"><RecoveryBanner at={offer.at} onKeep={keep} onDiscard={discard} /></div>}
      {children}
    </form>
  );
}

/** Remember one controlled text value, such as a note. */
export function useRecoveredText(key: string, value: string, setValue: (value: string) => void): { offer: Stored | null; keep: () => void; discard: () => void } {
  const [offer, setOffer] = useState<Stored | null>(null);
  const timer = useRef<number | null>(null);
  useEffect(() => {
    const stored = read(key);
    if (stored !== null && (stored.values["value"] ?? "") !== value && (stored.values["value"] ?? "") !== "") setOffer(stored);
    // Offer once, at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => write(key, { value }), DEBOUNCE_MS);
    return () => { if (timer.current !== null) window.clearTimeout(timer.current); };
  }, [key, value]);
  return {
    offer,
    keep: () => { if (offer) setValue(offer.values["value"] ?? ""); setOffer(null); },
    discard: () => { clearRecovery(key); setOffer(null); },
  };
}

/**
 * Remember several controlled text values at once: a map of reasons keyed by
 * record, or the text fields of a form object. Only strings are kept.
 */
export function useRecoveredValues<T extends Record<string, unknown>>(
  key: string, values: T, setValues: (next: T) => void,
): { offer: Stored | null; keep: () => void; discard: () => void } {
  const [offer, setOffer] = useState<Stored | null>(null);
  const timer = useRef<number | null>(null);
  const strings = (input: T): Record<string, string> =>
    Object.fromEntries(Object.entries(input).filter((entry): entry is [string, string] => typeof entry[1] === "string"));
  useEffect(() => {
    const stored = read(key);
    if (stored === null) return;
    const current = strings(values);
    const differs = Object.entries(stored.values).some(([name, value]) => (current[name] ?? "") !== value);
    if (differs) setOffer(stored);
    // Offer once, at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => write(key, strings(values)), DEBOUNCE_MS);
    return () => { if (timer.current !== null) window.clearTimeout(timer.current); };
  }, [key, values]);
  return {
    offer,
    keep: () => { if (offer) setValues({ ...values, ...offer.values } as T); setOffer(null); },
    discard: () => { clearRecovery(key); setOffer(null); },
  };
}
